import React, { useState, useRef, useEffect } from 'react';
import { useInView, AnimatePresence } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { Scale, Calculator, Heart, Sparkles, Zap } from 'lucide-react';
import BMICalculator from './tools/BMICalculator';
import MacroCalculator from './tools/MacroCalculator';
import HealthScoreQuiz from './tools/HealthScoreQuiz';

const tools = [
  {
    id: 'bmi',
    label: 'BMI Calculator',
    shortLabel: 'BMI',
    icon: Scale,
    color: 'from-purple-500 to-violet-600',
    glowColor: 'rgba(147, 51, 234, 0.4)',
  },
  {
    id: 'macro',
    label: 'Macro Calculator',
    shortLabel: 'Macros',
    icon: Calculator,
    color: 'from-violet-500 to-fuchsia-600',
    glowColor: 'rgba(168, 85, 247, 0.4)',
  },
  {
    id: 'quiz',
    label: 'Health Score',
    shortLabel: 'Health Score',
    icon: Heart,
    color: 'from-fuchsia-500 to-pink-600',
    glowColor: 'rgba(217, 70, 239, 0.4)',
  },
];

// Animated Background
const AnimatedBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <motion.div
      className="absolute w-[600px] h-[600px] rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(147, 51, 234, 0.12) 0%, transparent 70%)',
        top: '-10%',
        right: '-15%',
      }}
      animate={{
        scale: [1, 1.2, 1],
        x: [0, 30, 0],
        y: [0, -20, 0],
      }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute w-[500px] h-[500px] rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(217, 70, 239, 0.08) 0%, transparent 70%)',
        bottom: '-5%',
        left: '-10%',
      }}
      animate={{
        scale: [1.1, 1, 1.1],
        x: [0, -20, 0],
        y: [0, 30, 0],
      }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />

    <svg className="absolute inset-0 w-full h-full opacity-[0.02]">
      <defs>
        <pattern id="toolsGrid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="1" className="text-ston-purple" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#toolsGrid)" />
    </svg>

    {[Zap, Scale, Calculator].map((Icon, i) => (
      <motion.div
        key={i}
        className="absolute text-ston-purple/10"
        style={{
          left: `${15 + i * 35}%`,
          top: `${20 + i * 20}%`,
        }}
        animate={{
          y: [0, -20, 0],
          rotate: [0, 10, -10, 0],
          opacity: [0.05, 0.1, 0.05],
        }}
        transition={{
          duration: 5 + i,
          repeat: Infinity,
          delay: i * 0.5,
          ease: "easeInOut",
        }}
      >
        <Icon size={40 + i * 10} />
      </motion.div>
    ))}
  </div>
);

// Wrapper for tool content with animated border and glowing container effect
const ToolContainer = ({ children, activeTab }) => {
  const topRef = useRef(null);
  const rightRef = useRef(null);
  const bottomRef = useRef(null);
  const leftRef = useRef(null);

  useEffect(() => {
    const animateBorder = () => {
      const now = Date.now() / 1000;
      const speed = 0.5;

      const topX = Math.sin(now * speed) * 100;
      const rightY = Math.cos(now * speed) * 100;
      const bottomX = Math.sin(now * speed + Math.PI) * 100;
      const leftY = Math.cos(now * speed + Math.PI) * 100;

      if (topRef.current) topRef.current.style.transform = `translateX(${topX}%)`;
      if (rightRef.current) rightRef.current.style.transform = `translateY(${rightY}%)`;
      if (bottomRef.current) bottomRef.current.style.transform = `translateX(${bottomX}%)`;
      if (leftRef.current) leftRef.current.style.transform = `translateY(${leftY}%)`;

      requestAnimationFrame(animateBorder);
    };

    const animationId = requestAnimationFrame(animateBorder);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Color mapping per tab
  const borderColors = {
    bmi: { primary: 'rgba(147, 51, 234, 0.6)', secondary: 'rgba(139, 92, 246, 0.6)' },
    macro: { primary: 'rgba(139, 92, 246, 0.6)', secondary: 'rgba(217, 70, 239, 0.6)' },
    quiz: { primary: 'rgba(217, 70, 239, 0.6)', secondary: 'rgba(236, 72, 153, 0.6)' },
  };
  const colors = borderColors[activeTab] || borderColors.bmi;

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      {/* Outer glow */}
      <motion.div
        className="absolute -inset-4 rounded-3xl opacity-30 transition-colors duration-500"
        style={{
          background: activeTab === 'bmi'
            ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.3), rgba(139, 92, 246, 0.2))'
            : activeTab === 'macro'
              ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(217, 70, 239, 0.2))'
              : 'linear-gradient(135deg, rgba(217, 70, 239, 0.3), rgba(236, 72, 153, 0.2))',
        }}
      />

      {/* Container */}
      <div className="relative bg-ston-card/95 border border-ston-border rounded-2xl md:rounded-3xl overflow-hidden">
        {/* Animated border — Top */}
        <div className="absolute top-0 left-0 w-full h-[2px] overflow-hidden z-20">
          <div
            ref={topRef}
            className="absolute top-0 left-0 w-full h-full"
            style={{ background: `linear-gradient(to right, transparent, ${colors.primary}, transparent)` }}
          />
        </div>
        {/* Animated border — Right */}
        <div className="absolute top-0 right-0 w-[2px] h-full overflow-hidden z-20">
          <div
            ref={rightRef}
            className="absolute top-0 left-0 w-full h-full"
            style={{ background: `linear-gradient(to bottom, transparent, ${colors.secondary}, transparent)` }}
          />
        </div>
        {/* Animated border — Bottom */}
        <div className="absolute bottom-0 left-0 w-full h-[2px] overflow-hidden z-20">
          <div
            ref={bottomRef}
            className="absolute top-0 left-0 w-full h-full"
            style={{ background: `linear-gradient(to right, transparent, ${colors.primary}, transparent)` }}
          />
        </div>
        {/* Animated border — Left */}
        <div className="absolute top-0 left-0 w-[2px] h-full overflow-hidden z-20">
          <div
            ref={leftRef}
            className="absolute top-0 left-0 w-full h-full"
            style={{ background: `linear-gradient(to bottom, transparent, ${colors.secondary}, transparent)` }}
          />
        </div>

        {/* Corner glow dots */}
        <div className="absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse z-20" style={{ background: colors.primary }} />
        <div className="absolute bottom-3 left-3 w-2 h-2 rounded-full animate-pulse z-20" style={{ background: colors.secondary }} />

        {/* Inner gradient overlay */}
        <motion.div
          className="absolute inset-0 rounded-2xl md:rounded-3xl opacity-50"
          style={{
            background: 'linear-gradient(135deg, transparent 40%, rgba(147, 51, 234, 0.1) 100%)',
          }}
        />

        {/* Corner glow blurs */}
        <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full z-0" style={{ background: `${colors.primary.replace('0.6', '0.08')}` }} />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full z-0" style={{ background: `${colors.secondary.replace('0.6', '0.08')}` }} />

        <div className="relative z-10 p-4 md:p-8 lg:p-12">
          {children}
        </div>
      </div>
    </motion.div>
  );
};

// Compact Premium Tab Bar (shared by mobile and desktop, responsive sizing)
const TabBar = ({ activeTab, setActiveTab, isInView }) => (
  <motion.div
    className="mb-8 md:mb-12"
    initial={{ opacity: 0, y: 20 }}
    animate={isInView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.5, delay: 0.3 }}
  >
    <div className="relative flex w-full max-w-2xl mx-auto bg-ston-surface/60 backdrop-blur-md rounded-2xl p-1.5 border border-ston-border">
      {/* Animated pill background */}
      <motion.div
        className="absolute top-1.5 bottom-1.5 rounded-xl shadow-lg"
        layout
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        style={{
          width: `calc(${100 / tools.length}% - 4px)`,
          left: `calc(${(tools.findIndex(t => t.id === activeTab)) * (100 / tools.length)}% + 2px)`,
          background: activeTab === 'bmi'
            ? 'linear-gradient(135deg, #9333ea, #7c3aed)'
            : activeTab === 'macro'
              ? 'linear-gradient(135deg, #8b5cf6, #d946ef)'
              : 'linear-gradient(135deg, #d946ef, #ec4899)',
          boxShadow: activeTab === 'bmi'
            ? '0 4px 20px rgba(147, 51, 234, 0.4)'
            : activeTab === 'macro'
              ? '0 4px 20px rgba(168, 85, 247, 0.4)'
              : '0 4px 20px rgba(217, 70, 239, 0.4)',
        }}
      />
      {tools.map((tool) => {
        const Icon = tool.icon;
        const isActive = activeTab === tool.id;
        return (
          <motion.button
            key={tool.id}
            onClick={() => setActiveTab(tool.id)}
            className="relative z-10 flex-1 flex items-center justify-center gap-2 py-3 md:py-3.5 rounded-xl text-sm md:text-[15px] font-semibold transition-colors duration-200"
            style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.45)' }}
            whileHover={{ scale: isActive ? 1 : 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tool.label}</span>
            <span className="sm:hidden">{tool.shortLabel}</span>
          </motion.button>
        );
      })}
    </div>
  </motion.div>
);

const getInitialTool = () => {
  if (typeof window === 'undefined') return 'bmi';
  const requested = new URLSearchParams(window.location.search).get('tool');
  return tools.some((tool) => tool.id === requested) ? requested : 'bmi';
};

const FreeTools = () => {
  const [activeTab, setActiveTab] = useState(getInitialTool);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Deep links (e.g. /?tool=quiz#tools from link.ston.fit) need a corrected
  // scroll: the animated sections above shift layout after the browser's
  // native hash jump, so re-align once mounted and again once settled.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsTools = params.has('tool') || window.location.hash === '#tools';
    if (!wantsTools) return undefined;

    const scrollToSection = () => sectionRef.current?.scrollIntoView({ block: 'start' });
    const timers = [setTimeout(scrollToSection, 250), setTimeout(scrollToSection, 1200)];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <section
      id="tools"
      ref={sectionRef}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="free-tools-section"
    >
      <div className="absolute inset-0 bg-ston-bg" />
      <AnimatedBackground />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ston-purple/10 border border-ston-purple/20 mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.05 }}
          >
            <Sparkles className="w-4 h-4 text-ston-purple-light" />
            <span className="text-sm font-medium text-ston-purple-light">100% FREE</span>
          </motion.div>

          <motion.h2
            className="font-outfit font-bold text-ston-text leading-tight"
            style={{
              fontSize: 'clamp(36px, 6vw, 64px)',
              letterSpacing: '-3px'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Free tools.<br />
            <span className="relative">
              <span className="gradient-text">The app's free too.</span>
              <motion.div
                className="absolute -bottom-2 left-0 h-1 rounded-full bg-gradient-to-r from-ston-purple via-violet-500 to-fuchsia-500"
                initial={{ width: 0 }}
                animate={isInView ? { width: "100%" } : {}}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </span>
          </motion.h2>

          <motion.p
            className="mt-6 text-base md:text-lg text-ston-text-secondary max-w-[520px] mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            BMI, macros, a health score — handy calculators, right here on this
            page. But these are the keychain versions. The full instrument panel — AI
            logging, labs, recovery, a coach when you want one — lives in the
            app. Same price: nothing.
          </motion.p>
        </motion.div>

        {/* Compact Premium Tab Bar */}
        <TabBar activeTab={activeTab} setActiveTab={setActiveTab} isInView={isInView} />

        {/* Tool Content with Glowing Container */}
        <ToolContainer activeTab={activeTab}>
          <AnimatePresence mode="wait">
            {activeTab === 'bmi' && (
              <motion.div
                key="bmi"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <BMICalculator />
              </motion.div>
            )}
            {activeTab === 'macro' && (
              <motion.div
                key="macro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <MacroCalculator />
              </motion.div>
            )}
            {activeTab === 'quiz' && (
              <motion.div
                key="quiz"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <HealthScoreQuiz />
              </motion.div>
            )}
          </AnimatePresence>
        </ToolContainer>

        {/* The point of the samples: the full thing is free */}
        <motion.div
          className="mt-12 md:mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
        >
          <button
            type="button"
            onClick={() =>
              document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })
            }
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-ston-purple to-ston-purple-light px-8 py-4 text-[15px] font-semibold text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_36px_rgba(147,51,234,0.5)]"
            data-testid="tools-app-cta"
          >
            <span>Skip the samples — get the whole thing free</span>
            <span
              aria-hidden="true"
              className="transition-transform duration-300 group-hover:translate-x-0.5"
            >
              →
            </span>
          </button>
          <p className="mt-4 text-[13px] text-ston-text-muted">
            No card. No trial clock. The app is free.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FreeTools;
